export type DetectedImageType =
  | "image/jpeg"
  | "image/png"
  | "image/webp"
  | "image/gif"
  | "image/heic"
  | "image/heif"
  | "image/avif";

export const IMAGE_SIGNATURE_BYTES = 64;

const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const HEIC_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs"]);
const HEIF_BRANDS = new Set(["mif1", "msf1", "heif"]);
const AVIF_BRANDS = new Set(["avif", "avis"]);

function matchesAt(bytes: Uint8Array, signature: number[], offset = 0): boolean {
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

function asciiAt(bytes: Uint8Array, offset: number, length: number): string {
  if (offset + length > bytes.length) return "";
  return String.fromCharCode(...bytes.subarray(offset, offset + length));
}

function brandType(brand: string): DetectedImageType | null {
  if (AVIF_BRANDS.has(brand)) return "image/avif";
  if (HEIC_BRANDS.has(brand)) return "image/heic";
  if (HEIF_BRANDS.has(brand)) return "image/heif";
  return null;
}

function isoMediaType(bytes: Uint8Array): DetectedImageType | null {
  if (asciiAt(bytes, 4, 4) !== "ftyp") return null;
  const boxSize = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0);
  const brandsEnd = Math.min(boxSize, bytes.length);
  const brands = [asciiAt(bytes, 8, 4)];
  for (let offset = 16; offset + 4 <= brandsEnd; offset += 4) {
    brands.push(asciiAt(bytes, offset, 4));
  }
  const types = brands.map(brandType).filter((type) => type !== null);
  return types.find((type) => type !== "image/heif") ?? types[0] ?? null;
}

export function detectImageType(bytes: Uint8Array): DetectedImageType | null {
  if (bytes.length < 12) return null;
  if (matchesAt(bytes, JPEG_SIGNATURE)) return "image/jpeg";
  if (matchesAt(bytes, PNG_SIGNATURE)) return "image/png";
  if (asciiAt(bytes, 0, 4) === "RIFF" && asciiAt(bytes, 8, 4) === "WEBP") return "image/webp";
  if (asciiAt(bytes, 0, 4) === "GIF8") return "image/gif";
  return isoMediaType(bytes);
}

export async function withDetectedImageType(file: File): Promise<File> {
  const head = new Uint8Array(await file.slice(0, IMAGE_SIGNATURE_BYTES).arrayBuffer());
  const detected = detectImageType(head);
  if (!detected || detected === file.type) return file;
  return new File([file], file.name, { type: detected, lastModified: file.lastModified });
}
